import { restRequestThemePart } from './rest-api';
export class ThemePart {
    /**
     * Runs after theme, layout and all other modules were initialized.
     * Therefore it allows to find and directly communicate with other components
     */
    afterInit() { }
    beforeDestroy() { }
    /**
     * ThemePart constructor.
     * Re-declaring constructor in child classes is not recommended.
     * Implement <b>init</b> and <b>afterInit</b> methods instead
     */
    constructor(el) {
        this.$json$ = {};
        this.$subm$ = new ThemePart.SubmodulesList();
        this.$cleanupHandlers$ = [];
        this.$root$ = el;
        const jsonScript = this.getElementById('$json$');
        if (jsonScript && jsonScript.tagName === 'SCRIPT' && jsonScript.getAttribute('type') === 'application/json') {
            this.$json$ = JSON.parse(jsonScript.innerText);
        }
    }
    /**
     * Unbinds all event listeners and removes module from DOM (if not already removed).
     *
     * This doesn't remove any references made to this object made in custom code or outside of parts
     */
    destroy() {
        var _a;
        this.beforeDestroy();
        if (this.$cleanupHandlers$.length) {
            this.$cleanupHandlers$.forEach(h => h());
            this.$cleanupHandlers$ = [];
        }
        this.$subm$.list.forEach(p => p.destroy());
        this.$root$.dispatchEvent(new CustomEvent('ThemePart.destroy', { bubbles: false, cancelable: false }));
        (_a = this.$root$.parentElement) === null || _a === void 0 ? void 0 : _a.removeChild(this.$root$);
    }
    /**
     * Add cleanup handler which will be called on module destroy
     * @return function which should be used to call cleanup handler manually and remove it from cleanup list
     */
    addCleanupHandler(cleanup) {
        this.$cleanupHandlers$.push(cleanup);
        return () => {
            cleanup();
            this.$cleanupHandlers$ = this.$cleanupHandlers$.filter(h => h !== cleanup);
        };
    }
    /**
     * Root HTML Element of a module
     */
    get root() {
        return this.$root$;
    }
    /**
     * List of all modules which are <em>nested</em> within current module in terms of DOM hierarchy.
     */
    get submodules() {
        return this.$subm$;
    }
    /**
     * Module type name
     */
    get name() {
        return this.$root$.getAttribute(`data-${this.$partType$}`) || '';
    }
    /**
     * HTML ID attribute of a given module
     */
    get id() {
        return this.$root$.id;
    }
    /**
     * Get static JSON data stored using nested script container (when provided)
     */
    get json() {
        return this.$json$;
    }
    get dataProps() {
        return this.$root$.dataset;
    }
    async restRequest(methodOrArgs, action = '', args) {
        if (!methodOrArgs || typeof methodOrArgs === 'object') {
            return restRequestThemePart('GET', this.$partType$, this.name, '', methodOrArgs);
        }
        else {
            return restRequestThemePart(methodOrArgs, this.$partType$, this.name, action, args);
        }
    }
    findPart(partClass, recursive = true) {
        for (let part of this.$subm$.list) {
            if (part instanceof partClass) {
                return part;
            }
            else if (recursive) {
                const rec = part.findPart(partClass);
                if (rec) {
                    return rec;
                }
            }
        }
        return null;
    }
    findParts(partClass, recursive = true) {
        const acc = [];
        for (let part of this.$subm$.list) {
            if (part instanceof partClass) {
                acc.push(part);
            }
            else if (recursive) {
                const recParts = part.findParts(partClass);
                if (recParts.length) {
                    acc.splice(acc.length, 0, ...recParts);
                }
            }
        }
        return acc;
    }
    getId(idSuffix) {
        return this.id ? `${this.id}${idSuffix ? `-${idSuffix}` : ''}` : '';
    }
    /**
     * Select inner element by id by a given suffix
     */
    getElementById(idSuffix) {
        return document.getElementById(this.id ? `${this.id}-${idSuffix}` : idSuffix);
    }
    querySelector(selector) {
        return this.$root$.querySelector(selector);
    }
    querySelectorAll(selectors) {
        return [].slice.apply(this.$root$.querySelectorAll(selectors));
    }
    /**
     * Get prop from dataset
     */
    getDataProp(variableName, defaultValue) {
        return typeof this.dataProps[variableName] === 'undefined'
            ? defaultValue
            : this.dataProps[variableName];
    }
    getDataPropInt(variableName, defaultValue) {
        const value = this.dataProps[variableName];
        return typeof value !== 'undefined' && value !== null ? Number.parseInt(value) : defaultValue;
    }
    getDataPropNumber(variableName, defaultValue) {
        return defaultValue ? this.getDataPropFloat(variableName, defaultValue) : this.getDataPropFloat(variableName);
    }
    getDataPropFloat(variableName, defaultValue) {
        const value = this.dataProps[variableName];
        return typeof value !== 'undefined' && value !== null ? Number.parseFloat(value) : defaultValue;
    }
    getDataPropBool(variableName, defaultValue) {
        const value = this.dataProps[variableName];
        if (value) {
            if (value === '1' || value.match(/^(true|yes|on|enabled?)$/i)) {
                return true;
            }
            else if (value === '0' || value.match(/^(false|no|off|disabled?)$/i)) {
                return false;
            }
            else {
                // Invalid value
                return defaultValue !== null && defaultValue !== void 0 ? defaultValue : undefined;
            }
        }
        // Empty value equates to false
        return false;
    }
}
/**
 * Class definition for module without any specific ClassModule implementation
 */
ThemePart.UnknownPart = class UnknownPart extends ThemePart {
    init() { }
    ;
    get $partType$() { return 'tmod'; }
};
ThemePart.SubmodulesList = class SubmodulesList {
    constructor() {
        this.$arr$ = [];
    }
    add(module) {
        module.root.addEventListener('ThemePart.destroy', () => {
            this.$arr$ = this.$arr$.filter(p => p !== module);
        }, { once: true });
        this.$arr$.push(module);
    }
    get list() {
        return [...this.$arr$];
    }
    get all() {
        const acc = [];
        for (let module of this.$arr$) {
            acc.push(module);
            acc.splice(acc.length, 0, ...module.submodules.all);
        }
        return acc;
    }
};
